import { filterRowsByTeam } from '../../detectTeamRows';
import normalizeOcrText from '../../normalizeOcrText';
import { getControlLayout } from './layouts';

const FIELD_OCR_CONFIG = {
  player: {
    psm: '7',
    whitelist: '',
  },
  operatorValue: {
    psm: '7',
    whitelist: 'xX0123456789/ ',
  },
};

const getConfigSignature = (config) => `${config.psm}|${config.whitelist}`;

const setWorkerParameters = async (worker, fieldKey, lastSignatureRef) => {
  const config = FIELD_OCR_CONFIG[fieldKey] || FIELD_OCR_CONFIG.player;
  const signature = getConfigSignature(config);

  if (lastSignatureRef.current === signature) {
    return;
  }

  await worker.setParameters({
    tessedit_pageseg_mode: config.psm,
    tessedit_char_whitelist: config.whitelist,
    preserve_interword_spaces: '1',
  });
  lastSignatureRef.current = signature;
};

const cropCanvas = ({ canvas, row, fieldBounds, padding = 2 }) => {
  const sourceX = row.bounds.x + Math.round(row.bounds.width * fieldBounds.xStart) - padding;
  const sourceY = row.bounds.y + Math.round(row.bounds.height * fieldBounds.yStart) - padding;
  const sourceWidth = Math.round(row.bounds.width * (fieldBounds.xEnd - fieldBounds.xStart)) + (padding * 2);
  const sourceHeight = Math.round(row.bounds.height * (fieldBounds.yEnd - fieldBounds.yStart)) + (padding * 2);
  const sx = Math.max(0, sourceX);
  const sy = Math.max(0, sourceY);
  const sw = Math.min(canvas.width - sx, sourceWidth);
  const sh = Math.min(canvas.height - sy, sourceHeight);
  const output = document.createElement('canvas');
  const context = output.getContext('2d', { willReadFrequently: true });

  if (!context) {
    throw new Error('Could not create Prop Details field crop canvas.');
  }

  output.width = Math.max(1, sw);
  output.height = Math.max(1, sh);
  context.drawImage(canvas, sx, sy, sw, sh, 0, 0, output.width, output.height);

  return {
    canvas: output,
    bounds: { x: sx, y: sy, width: output.width, height: output.height },
    previewUrl: output.toDataURL('image/png'),
  };
};

const recognizeField = async ({
  worker,
  canvas,
  fieldKey,
  lastSignatureRef,
}) => {
  await setWorkerParameters(worker, fieldKey, lastSignatureRef);
  const result = await worker.recognize(canvas, {}, { text: true });
  const data = result?.data || {};

  return {
    raw: normalizeOcrText(data.text || ''),
    confidence: typeof data.confidence === 'number' ? data.confidence : null,
  };
};

const getName = (raw, warnings) => {
  const detectedName = normalizeOcrText(raw).replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!detectedName) {
    warnings.push('Player name was empty.');
  }
  return detectedName;
};

const parseOperatorValue = (raw, warnings) => {
  const text = String(raw || '').trim();
  if (!text) {
    warnings.push('Operator Value failed from empty OCR.');
    return null;
  }

  const usageMatches = Array.from(text.matchAll(/x\s*(\d+)/gi));
  const usageValues = new Set(usageMatches.map((match) => Number.parseInt(match[1], 10)));
  const withoutUsageCounts = text.replace(/x\s*\d+/gi, ' ');
  const remainingNumbers = Array.from(withoutUsageCounts.matchAll(/\d+/g))
    .map((match) => Number.parseInt(match[0], 10))
    .filter((value) => Number.isInteger(value) && value >= 0);

  if (remainingNumbers.length === 1) {
    return remainingNumbers[0];
  }

  if (remainingNumbers.length === 2) {
    const sorted = [...remainingNumbers].sort((a, b) => a - b);
    if (sorted[0] <= 10 && sorted[1] > 10) {
      return sorted[1];
    }
  }

  const numericCandidates = Array.from(text.matchAll(/\d+/g))
    .map((match) => Number.parseInt(match[0], 10))
    .filter((value) => Number.isInteger(value) && value >= 0);
  const valueCandidates = numericCandidates.filter((value) => value > 10 && !usageValues.has(value));

  if (valueCandidates.length === 1) {
    return valueCandidates[0];
  }

  warnings.push(`Operator Value was ambiguous from "${raw || ''}".`);
  return null;
};

const parsePropDetails = async ({
  worker,
  processedImage,
  detection,
  selectedTeamColor = 'blue',
  sourceScreenshotIndex = 0,
  sourceScreenshotName = 'Prop Details',
}) => {
  if (!worker) {
    throw new Error('OCR worker is not ready.');
  }

  if (!processedImage?.canvas) {
    throw new Error('Processed Prop Details image is required.');
  }

  const startedAt = performance.now();
  const layout = getControlLayout('propDetails');
  const selectedRows = filterRowsByTeam(detection?.rows || [], selectedTeamColor)
    .sort((a, b) => (a.bounds.y - b.bounds.y) || (a.bounds.x - b.bounds.x));
  const records = [];
  const warnings = [];
  const lastSignatureRef = { current: null };
  let fieldCropCount = 0;

  if (!selectedRows.length) {
    warnings.push(`No ${selectedTeamColor.toUpperCase()} Prop Details rows were detected in this screenshot.`);
  }

  for (const row of selectedRows) {
    const rowWarnings = [];
    const crops = {};
    const fieldResults = {};

    for (const fieldKey of ['player', 'operatorValue']) {
      const crop = cropCanvas({
        canvas: processedImage.canvas,
        row,
        fieldBounds: layout.fields[fieldKey],
      });
      fieldCropCount += 1;
      crops[fieldKey] = crop;
      fieldResults[fieldKey] = await recognizeField({
        worker,
        canvas: crop.canvas,
        fieldKey,
        lastSignatureRef,
      });
    }

    records.push({
      sourceRowId: row.id,
      sourceRowIndex: row.rowIndex,
      sourceScreenshotIndex,
      sourceScreenshotName,
      detectedName: getName(fieldResults.player.raw, rowWarnings),
      operatorValue: parseOperatorValue(fieldResults.operatorValue.raw, rowWarnings),
      _ocr: fieldResults,
      _warnings: rowWarnings,
      _crops: Object.fromEntries(Object.entries(crops).map(([key, crop]) => ([
        key,
        {
          bounds: crop.bounds,
          previewUrl: crop.previewUrl,
        },
      ]))),
    });
  }

  return {
    category: 'propDetails',
    selectedTeamColor,
    selectedRowCount: selectedRows.length,
    fieldCropCount,
    elapsedMs: Math.round(performance.now() - startedAt),
    sourceScreenshotIndex,
    sourceScreenshotName,
    warnings,
    records,
  };
};

export default parsePropDetails;
